/**
 * @file subscriptionApi.ts
 * @description RTK Query API slice for subscription management.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import type { SubscriptionSummary, UserPlan } from '@forex-builder/shared-types';
import { createBaseQueryWithReauth } from '../../../shared/api/baseQueryWithReauth.js';
import type { RootState } from '../../../app/store.js';
import { updateUser } from '../../auth/model/authSlice.js';

export interface SubscriptionSummaryResponse {
  success: boolean;
  data: SubscriptionSummary;
}

export interface SwitchPlanRequest {
  planId: UserPlan;
}

export interface SwitchPlanResponse {
  success: boolean;
  message: string;
  data: SubscriptionSummary;
}

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: createBaseQueryWithReauth('/api/v1/subscription'),
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ['Subscription'],
  endpoints: (builder) => ({
    getSubscriptionSummary: builder.query<SubscriptionSummaryResponse, void>({
      query: () => '/me',
      providesTags: ['Subscription'],
    }),

    switchPlan: builder.mutation<SwitchPlanResponse, SwitchPlanRequest>({
      query: (body) => ({
        url: '/switch-plan',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscription'],
      async onQueryStarted({ planId }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled;
          const state = getState() as RootState;
          if (state.auth?.user) {
            dispatch(updateUser({ ...state.auth.user, plan: planId }));
          }
        } catch {
          // Request failed
        }
      },
    }),
  }),
});

export const {
  useGetSubscriptionSummaryQuery,
  useSwitchPlanMutation,
} = subscriptionApi;
